<template>
  <div
    class="comment_item"
    :class="{ 'comment_item--highlight': isHighlighted(data) }"
    :data-forum-comment-id="getCommentId(data)"
  >
    <div class="comment_item__avatar_wrap">
      <div class="comment_item__avatar"><img :src="data.avatar || FORUM_DEFAULT_AVATAR" alt="" /></div>
      <img v-if="levelFrame && levelFrame.frameUrl" class="comment_item__frame" :src="levelFrame.frameUrl" alt="" />
    </div>

    <div class="comment_item__body">
      <div class="comment_item__user">
        <span class="comment_item__name text_ellipsis">{{ data.nickname || '昵称为空' }}</span>
        <span v-if="isOwner(data.uid)" class="comment_item__owner_tag">楼主</span>
        <span v-if="showFloor" class="comment_item__floor">{{ data.floor }}楼</span>
        <span v-if="levelFrame && levelFrame.nameplate" class="comment_item__nameplate" :style="{ backgroundImage: `url(${levelFrame.nameplate})` }" />
        <span v-if="data.hotComment" class="comment_item__hot_tag"><van-icon name="fire-o" />热门评论</span>
      </div>

      <div class="comment_item__main" :class="{ 'comment_item__main--hot': data.hotComment }">
        <p v-if="data.content" class="comment_item__text">{{ data.content }}</p>
        <img
          v-if="imageList[0]"
          :src="imageList[0]"
          class="comment_item__img"
          alt=""
          @click="previewImage(imageList[0], $event)"
        />
        <div class="comment_item__action">
          <span class="comment_item__time">{{ data.timeDesc }}</span>
          <button type="button" class="comment_item__act_btn" @click="onReply">
            <van-icon name="comment-o" class="comment_item__act_icon" color="#c2c2c2" />
            <span class="comment_item__act_count">{{ replyTotal }}</span>
          </button>
          <button type="button" class="comment_item__act_btn" @click="onPraise(data, $event)">
            <van-icon :name="data.praise ? 'good-job' : 'good-job-o'" class="comment_item__act_icon" :color="data.praise ? '#ff7352' : '#c2c2c2'" />
            <span class="comment_item__act_count" :class="{ 'comment_item__act_count--active': data.praise }">{{ data.praiseNum || 0 }}</span>
          </button>
        </div>

        <!-- 子评论列表：默认展示 2 条，展开后展示全部 -->
        <template v-if="replyList.length || expanded">
          <div v-if="loading" class="comment_item__sub_loading">加载中…</div>
          <div
            v-for="sub in displayedReplyList"
            :key="getCommentId(sub)"
            class="comment_item__sub"
            :class="{ 'comment_item__sub--highlight': isHighlighted(sub) }"
            :data-forum-comment-id="getCommentId(sub)"
          >
            <div class="comment_item__sub_avatar_wrap">
              <div class="comment_item__sub_avatar"><img :src="sub.avatar || FORUM_DEFAULT_AVATAR" alt="" /></div>
              <img v-if="sub.userLevelFrameInfoVo && sub.userLevelFrameInfoVo.frameUrl" class="comment_item__sub_frame" :src="sub.userLevelFrameInfoVo.frameUrl" alt="" />
            </div>
            <div class="comment_item__sub_body">
              <div class="comment_item__sub_user">
                <span class="comment_item__sub_name text_ellipsis">{{ sub.nickname || '昵称为空' }}</span>
                <span v-if="isOwner(sub.uid)" class="comment_item__owner_tag">楼主</span>
                <span v-if="sub.userLevelFrameInfoVo && sub.userLevelFrameInfoVo.nameplate" class="comment_item__sub_nameplate" :style="{ backgroundImage: `url(${sub.userLevelFrameInfoVo.nameplate})` }" />
                <template v-if="sub.replyNickname || sub.parentNickname">
                  <span class="comment_item__sub_reply_word">回复了</span>
                  <span class="comment_item__sub_reply_name text_ellipsis">{{ sub.replyNickname || sub.parentNickname }}</span>
                </template>
              </div>
              <div class="comment_item__sub_main" @click="onReplySub(sub)">
                <p v-if="sub.content" class="comment_item__text">{{ sub.content }}</p>
                <img
                  v-if="getImageList(sub)[0]"
                  :src="getImageList(sub)[0]"
                  class="comment_item__img"
                  alt=""
                  @click="previewImage(getImageList(sub)[0], $event)"
                />
                <div class="comment_item__action">
                  <span class="comment_item__time">{{ sub.timeDesc }}</span>
                  <button type="button" class="comment_item__act_btn" @click="onReplySubClick(sub, $event)">
                    <van-icon name="comment-o" class="comment_item__act_icon" color="#c2c2c2" />
                  </button>
                  <button type="button" class="comment_item__act_btn" @click="onPraise(sub, $event)">
                    <van-icon :name="sub.praise ? 'good-job' : 'good-job-o'" class="comment_item__act_icon" :color="sub.praise ? '#ff7352' : '#c2c2c2'" />
                    <span class="comment_item__act_count" :class="{ 'comment_item__act_count--active': sub.praise }">{{ sub.praiseNum || 0 }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <button v-if="showReplyToggle" type="button" class="comment_item__toggle" @click="onToggleReplies">
          {{ expanded ? '收起回复' : `展开 ${replyTotal} 条回复` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon, ImagePreview } from 'vant';
import { getCommentList } from '@/api/forum';
import { FORUM_DEFAULT_AVATAR } from '../constants';

/** 默认展示子评论条数，超出后显示「展开 n 条回复」 */
const DEFAULT_REPLY_VISIBLE_COUNT = 2;

export default {
  name: 'ForumCommentItem',
  components: { [Icon.name]: Icon },
  props: {
    data: {
      type: Object,
      required: true,
    },
    postId: [String, Number],
    ownerUid: [String, Number],
    replyCount: Number,
    highlightCommentId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      FORUM_DEFAULT_AVATAR,
      expanded: false,
      loading: false,
      replyList: this.data.replyList || [],
      replyRequestSeq: 0,
    };
  },
  computed: {
    showFloor() {
      return Number(this.data.floor) > 0;
    },
    replyTotal() {
      return Math.max(
        this.replyCount || 0,
        this.data.replayNum || 0,
        this.data.replyNum || 0,
        this.replyList.length,
      );
    },
    displayedReplyList() {
      return this.expanded || this.containsHighlightedReply
        ? this.replyList
        : this.replyList.slice(0, DEFAULT_REPLY_VISIBLE_COUNT);
    },
    containsHighlightedReply() {
      return Boolean(this.highlightCommentId)
        && this.replyList.some((item) => String(this.getCommentId(item)) === this.highlightCommentId);
    },
    showReplyToggle() {
      return this.replyTotal > DEFAULT_REPLY_VISIBLE_COUNT;
    },
    imageList() {
      return this.getImageList(this.data);
    },
    levelFrame() {
      return this.data.userLevelFrameInfoVo || null;
    },
  },
  watch: {
    data: {
      async handler(val) {
        this.replyList = (val && val.replyList) || [];
        this.expandHighlightedReply();
        if (this.expanded && !this.replyList.length && this.replyTotal) {
          await this.fetchReplies();
        }
      },
      deep: true,
    },
    highlightCommentId: {
      handler() {
        this.expandHighlightedReply();
      },
      immediate: true,
    },
  },
  methods: {
    expandHighlightedReply() {
      if (this.containsHighlightedReply) this.expanded = true;
    },
    getCommentId(item = {}) {
      return item.id || '';
    },
    isHighlighted(item) {
      return Boolean(this.highlightCommentId)
        && String(this.getCommentId(item)) === this.highlightCommentId;
    },
    isOwner(uid) {
      return uid !== undefined && this.ownerUid !== undefined && String(uid) === String(this.ownerUid);
    },
    getImageList(item = {}) {
      const list = item.imgList || item.imageList || [];
      return list.map((img) => (typeof img === 'string' ? img : img.url || img.picUrl)).filter(Boolean);
    },
    isReplyOfCurrentComment(reply, currentCommentId) {
      const parentId = String(reply.parentCommentId || '');
      const rootCommentId = String(reply.commentId || '');
      const currentId = String(currentCommentId);
      return parentId !== '' && parentId !== '0' && (parentId === currentId || rootCommentId === currentId);
    },
    async fetchReplies() {
      const currentCommentId = this.getCommentId(this.data);
      if (!currentCommentId) return;
      const requestSeq = ++this.replyRequestSeq;
      this.loading = true;
      try {
        const res = await getCommentList({
          postId: this.postId,
          current: 1,
          size: 20,
        });
        if (requestSeq !== this.replyRequestSeq) return;
        const records = (res && res.data && (res.data.commentList || res.data.records || res.data.list)) || [];
        this.replyList = records.filter((item) => this.isReplyOfCurrentComment(item, currentCommentId));
      } finally {
        if (requestSeq === this.replyRequestSeq) this.loading = false;
      }
    },
    async onToggleReplies() {
      if (this.expanded) {
        this.expanded = false;
        return;
      }
      this.expanded = true;
      if (!this.replyTotal) return;
      if (this.replyList.length >= this.replyTotal) return;
      await this.fetchReplies();
    },
    onReply(event) {
      event.stopPropagation();
      this.$emit('reply', this.data);
    },
    onReplySub(sub) {
      this.$emit('reply-sub', { parent: this.data, sub });
    },
    onReplySubClick(sub, event) {
      event.stopPropagation();
      this.$emit('reply-sub', { parent: this.data, sub });
    },
    onPraise(item, event) {
      event.stopPropagation();
      this.$emit('praise', item);
    },
    previewImage(url, event) {
      event.stopPropagation();
      ImagePreview([url]);
    },
  },
};
</script>

<style scoped>
.comment_item {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  transition: background-color 0.4s ease, box-shadow 0.4s ease;
}

.comment_item--highlight,
.comment_item__sub--highlight {
  background: #fff1d6;
}

.comment_item__avatar_wrap {
  position: relative;
  width: 34px;
  height: 34px;
  margin-right: 6px;
  flex-shrink: 0;
}

.comment_item__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e5e5;
}

.comment_item__frame {
  position: absolute;
  top: -6px;
  left: -6px;
  width: 46px;
  max-width: none;
  height: 46px;
  object-fit: contain;
  pointer-events: none;
}

.comment_item__avatar > img,
.comment_item__sub_avatar > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.comment_item__avatar_fallback {
  width: 100%;
  height: 100%;
  background: #e5e5e5;
}

.comment_item__body {
  min-width: 0;
  flex: 1;
  text-align: left;
}

.comment_item__user {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.comment_item__name {
  max-width: 160px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.comment_item__owner_tag {
  display: inline-block;
  height: 14px;
  margin-left: 4px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 0 4px;
  color: #999;
  font-size: 10px;
  line-height: 14px;
}

.comment_item__floor {
  margin-left: 4px;
  color: #999;
  font-size: 11px;
  line-height: 14px;
}

.comment_item__hot_tag {
  display: inline-flex;
  align-items: center;
  height: 18px;
  margin-left: 5px;
  padding: 0 6px;
  color: #fff;
  font-size: 10px;
  line-height: 18px;
  background: linear-gradient(90deg, #ff7352, #ff4f9a);
  border-radius: 9px;
}

.comment_item__hot_tag .van-icon { margin-right: 2px; }

.comment_item__nameplate {
  width: 55px;
  height: 25px;
  margin-left: 6px;
  flex-shrink: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.comment_item__main {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 58px;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 8px 6px 5px;
  background: #f6f6f6;
}

.comment_item__main--hot {
  padding-left: 9px;
  background: #fff0f2;
  border-left: 3px solid #ff4f4f;
}

.comment_item__text {
  flex: 1;
  margin: 0 0 6px;
  color: #333;
  font-size: 12px;
  line-height: 18px;
  white-space: pre-line;
  word-break: break-word;
}

.comment_item__img {
  width: 60px;
  height: 60px;
  margin-bottom: 6px;
  border-radius: 4px;
  object-fit: cover;
}

.comment_item__action {
  display: flex;
  align-items: center;
}

.comment_item__time {
  flex: 1;
  color: #999;
  font-size: 12px;
  transform: scale(0.9);
  transform-origin: left center;
}

.comment_item__act_btn {
  display: flex;
  align-items: center;
  border: 0;
  padding: 0;
  background: transparent;
  line-height: 1;
}

.comment_item__act_btn + .comment_item__act_btn {
  margin-left: 10px;
}

.comment_item__act_icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  font-size: 12px;
  line-height: 12px;
}

.comment_item__act_count {
  color: #c2c2c2;
  font-size: 12px;
  line-height: 12px;
}

.comment_item__act_count--active {
  color: #ff7352;
}

.comment_item__sub_loading {
  margin-top: 8px;
  color: #999;
  font-size: 12px;
}

.comment_item__sub {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
  padding-left: 4px;
  border-radius: 4px;
  transition: background-color 0.4s ease, box-shadow 0.4s ease;
}

.comment_item__sub + .comment_item__sub {
  margin-top: 4px;
}

.comment_item__sub_avatar_wrap {
  position: relative;
  width: 27px;
  height: 27px;
  margin-right: 6px;
  flex-shrink: 0;
}

.comment_item__sub_avatar {
  width: 27px;
  height: 27px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e5e5;
}

.comment_item__sub_frame {
  position: absolute;
  top: -5px;
  left: -5px;
  width: 37px;
  max-width: none;
  height: 37px;
  object-fit: contain;
  pointer-events: none;
}

.comment_item__sub_nameplate {
  width: 44px;
  height: 20px;
  margin-left: 4px;
  flex-shrink: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.comment_item__sub_body {
  min-width: 0;
  flex: 1;
}

.comment_item__sub_user {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  overflow: hidden;
  min-width: 0;
  margin-bottom: 4px;
  padding: 0 3px;
}

.comment_item__sub_name {
  flex-shrink: 1;
  min-width: 0;
  max-width: 90px;
  color: #666;
  font-size: 12px;
}

.comment_item__sub_user .comment_item__owner_tag {
  flex-shrink: 0;
  white-space: nowrap;
}

.comment_item__sub_reply_word {
  flex-shrink: 0;
  white-space: nowrap;
  margin: 0 4px;
  color: #333;
  font-size: 12px;
}

.comment_item__sub_reply_name {
  flex-shrink: 1;
  min-width: 0;
  max-width: 90px;
  color: #666;
  font-size: 12px;
}

.comment_item__sub_main {
  border-radius: 4px;
  padding: 8px 6px 5px;
  background: #ececec;
}

.comment_item__toggle {
  align-self: flex-start;
  margin-top: 8px;
  border: 0;
  padding: 0;
  color: #6879ad;
  background: transparent;
  font-size: 12px;
}
</style>
